/**
 * ComponentPanel Component
 *
 * Side panel for managing label component visibility and lock state.
 * Groups components by category and shows validation feedback.
 *
 * Features:
 * - All 12 component types with human-readable labels
 * - Collapsible groups (Scannable, Order Info, Badges)
 * - Visibility toggle (disabled for required components)
 * - Lock toggle to prevent accidental moves
 * - Click to select component in canvas
 * - Validation feedback inline
 * - Collapsible panel for smaller screens
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock, Unlock, ChevronDown, ChevronRight, ChevronLeft, Layers } from 'lucide-react';
import { useLabelLayoutStore } from './label-layout-store';
import { COMPONENT_CONSTRAINTS, COMPONENT_LABEL_KEYS, type ComponentType } from './label-layout-types';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ValidationFeedback } from './ValidationFeedback';

// Group components by category for better organization
// Keys are stable identifiers (not displayed); display names come from i18n
const COMPONENT_GROUP_CONFIGS = [
  { key: 'scannable', i18nKey: 'production.componentPanel.groupScannable', types: ['qr', 'barcode', 'orderNumberBarcode'] as ComponentType[] },
  { key: 'orderInfo', i18nKey: 'production.componentPanel.groupOrderInfo', types: ['orderNumber', 'sizeColor', 'productInfo', 'productType', 'itemCount', 'binLabel', 'printArea'] as ComponentType[] },
  { key: 'badges', i18nKey: 'production.componentPanel.groupBadges', types: ['checkboxes', 'dtgIndicator', 'ptBadge', 'redoBadge'] as ComponentType[] },
];

const STORAGE_KEY = 'label-layout-component-panel-collapsed';

export function ComponentPanel() {
  const { t } = useTranslation('settings');
  // Initialize collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    scannable: true,
    orderInfo: true,
    badges: true,
  });

  // Persist collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
    } catch {
      // Ignore localStorage errors (e.g., quota exceeded)
    }
  }, [isCollapsed]);

  const components = useLabelLayoutStore((s) => s.components);
  const selectedId = useLabelLayoutStore((s) => s.editor.selectedComponentId);
  const labelType = useLabelLayoutStore((s) => s.labelType);
  const toggleVisibility = useLabelLayoutStore((s) => s.toggleVisibility);
  const updateComponent = useLabelLayoutStore((s) => s.updateComponent);
  const setSelectedComponent = useLabelLayoutStore((s) => s.setSelectedComponent);

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const getComponentByType = (type: ComponentType) => {
    return components.find((c) => c.type === type);
  };

  const isRequiredForLabelType = (type: ComponentType): boolean => {
    const constraints = COMPONENT_CONSTRAINTS[type];
    if (!constraints) return false;
    if (constraints.required) return true;
    if (!constraints.requiredFor) return false;
    return constraints.requiredFor.includes(labelType as 'POD' | 'P&P');
  };

  /**
   * Check if a component is available for the current label type.
   * Components unavailable for the label type should be disabled/hidden.
   *
   * Rules:
   * - binLabel: Only for P&P and BOTH (POD bins assigned at catcher AFTER printing)
   * - dtgIndicator: Only for POD and BOTH (P&P items don't use DTG)
   * - ptBadge: Only for POD and BOTH (pretreat only for DTG on dark garments)
   */
  const isAvailableForLabelType = (type: ComponentType): boolean => {
    const constraints = COMPONENT_CONSTRAINTS[type];
    if (!constraints?.availableFor) return true; // No restriction = available for all
    return constraints.availableFor.includes(labelType);
  };

  /**
   * Get a tooltip explaining why a component is unavailable.
   */
  const getUnavailableReason = (type: ComponentType): string | null => {
    if (isAvailableForLabelType(type)) return null;

    switch (type) {
      case 'binLabel':
        return t('production.componentPanel.unavailableBinLabel');
      case 'dtgIndicator':
        return t('production.componentPanel.unavailableDtgIndicator');
      case 'ptBadge':
        return t('production.componentPanel.unavailablePtBadge');
      case 'printArea':
        return t('production.componentPanel.unavailablePrintArea');
      default:
        return t('production.componentPanel.unavailableGeneric', { labelType });
    }
  };

  // Count visible and total components for collapsed indicator
  const visibleCount = components.filter((c) => c.visible).length;
  const totalCount = components.length;

  if (isCollapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={() => setIsCollapsed(false)}
              className="h-auto py-3 px-2 flex flex-col items-center gap-2 border-dashed border-2 hover:border-solid hover:border-primary/50 transition-all group"
            >
              <Layers className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              <div className="flex flex-col items-center text-xs">
                <span className="font-medium">{visibleCount}/{totalCount}</span>
                <span className="text-muted-foreground text-[10px]">{t('production.componentPanel.visible')}</span>
              </div>
              <ChevronLeft className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors animate-pulse" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p className="font-medium">{t('production.componentPanel.expandTooltip')}</p>
            <p className="text-xs text-muted-foreground">{t('production.componentPanel.expandTooltipDesc')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="border rounded-lg bg-card overflow-hidden w-64 shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/50">
        <h3 className="font-semibold text-sm">{t('production.componentPanel.title')}</h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10"
          onClick={() => setIsCollapsed(true)}
          title={t('production.componentPanel.collapsePanel')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Component Groups */}
      <div className="p-2 space-y-1 max-h-[400px] overflow-y-auto">
        {COMPONENT_GROUP_CONFIGS.map((group) => (
          <Collapsible
            key={group.key}
            open={expandedGroups[group.key]}
            onOpenChange={() => toggleGroup(group.key)}
          >
            <CollapsibleTrigger
              className="flex items-center gap-1 w-full p-1.5 text-xs font-medium text-muted-foreground hover:text-foreground rounded hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              aria-label={expandedGroups[group.key]
                ? t('production.componentPanel.groupAriaExpanded', { name: t(group.i18nKey) })
                : t('production.componentPanel.groupAriaCollapsed', { name: t(group.i18nKey) })
              }
            >
              {expandedGroups[group.key] ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {t(group.i18nKey)}
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-0.5 ml-2">
              {group.types.map((type) => {
                const comp = getComponentByType(type);
                if (!comp) return null;

                const isRequired = isRequiredForLabelType(type);
                const isSelected = selectedId === comp.id;
                const isAvailable = isAvailableForLabelType(type);
                const unavailableReason = getUnavailableReason(type);

                return (
                  <TooltipProvider key={comp.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'flex items-center justify-between p-1.5 rounded transition-colors',
                            !isAvailable && 'opacity-50 cursor-not-allowed',
                            isAvailable && 'cursor-pointer',
                            isSelected && isAvailable
                              ? 'bg-primary/10 border border-primary/30'
                              : 'hover:bg-muted border border-transparent'
                          )}
                          onClick={() => isAvailable && setSelectedComponent(comp.id)}
                          role="button"
                          tabIndex={isAvailable ? 0 : -1}
                          aria-disabled={!isAvailable}
                          onKeyDown={(e) => {
                            if (isAvailable && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              setSelectedComponent(comp.id);
                            }
                          }}
                        >
                          <span
                            className={cn(
                              'text-sm flex-1 truncate',
                              !comp.visible && 'text-muted-foreground line-through',
                              !isAvailable && 'text-muted-foreground'
                            )}
                          >
                            {t(COMPONENT_LABEL_KEYS[type])}
                            {isRequired && (
                              <span className="text-destructive ml-1" title={t('production.componentPanel.requiredForLabelType')}>
                                *
                              </span>
                            )}
                            {!isAvailable && (
                              <span className="text-muted-foreground ml-1 text-xs">({t('production.componentPanel.notAvailableForLabelType')})</span>
                            )}
                          </span>
                          <div className="flex gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isRequired && isAvailable) toggleVisibility(comp.id);
                              }}
                              disabled={isRequired || !isAvailable}
                              title={
                                !isAvailable
                                  ? unavailableReason || t('production.componentPanel.notAvailableForLabelType')
                                  : isRequired
                                    ? t('production.componentPanel.requiredForLabelType')
                                    : comp.visible
                                      ? t('production.componentPanel.hideComponent')
                                      : t('production.componentPanel.showComponent')
                              }
                            >
                              {comp.visible ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isAvailable) {
                                  updateComponent(comp.id, { locked: !comp.locked });
                                }
                              }}
                              disabled={!isAvailable}
                              title={
                                !isAvailable
                                  ? unavailableReason || t('production.componentPanel.notAvailableForLabelType')
                                  : comp.locked
                                    ? t('production.componentPanel.unlock')
                                    : t('production.componentPanel.lockPosition')
                              }
                            >
                              {comp.locked ? (
                                <Lock className="h-4 w-4 text-amber-500" />
                              ) : (
                                <Unlock className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </TooltipTrigger>
                      {!isAvailable && unavailableReason && (
                        <TooltipContent side="left" className="max-w-[200px]">
                          <p className="text-xs">{unavailableReason}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>

      {/* Validation Feedback */}
      <div className="p-2 border-t">
        <ValidationFeedback />
      </div>

      {/* Legend */}
      <div className="px-3 py-2 border-t bg-muted/30">
        <p className="text-xs text-muted-foreground">
          <span className="text-destructive">*</span> {t('production.componentPanel.requiredLegend')}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {t('production.componentPanel.arrowKeysLegend')}
        </p>
      </div>
    </div>
  );
}
